// ============================================================================
// Switching suites (2026-10-06): the rules behind the switcher, pure and
// tested (switch.test.ts). Where a switch lands, the order suites are offered
// in (which is also their ⌃1–⌃9 order), and the few apps a suite's card shows
// so suites are told apart at a glance.
// ============================================================================
import { ALL } from '@/kinds/workspace/schema'
import { navActive } from '@/lib/nav'
import { sanitise, storedPhone, type PhoneSettings } from '@/os/layout'

interface SuiteLike {
  spec: { slug: string; name: string; order: number }
  status: { archived_at?: string }
}

/** The live suites in the switcher's order: by `order`, then name. Their index is their shortcut. */
export const orderSuites = <W extends SuiteLike>(all: readonly W[]): W[] =>
  all
    .filter((w) => !w.status.archived_at)
    .sort((a, b) => a.spec.order - b.spec.order || a.spec.name.localeCompare(b.spec.name))

// a path that names one thing (a task, a page, an agent) belongs to the suite it came from
const ONE_THING = /^\/(tasks|projects|pages|lists|dates|people|captures|agents\/run|dev\/review)\/[^/]+/

/**
 * Where the app goes after a switch: the page you were on, now showing the
 * other suite's, when that suite has the app (Settings and Search everywhere;
 * Everything has every app, `apps` null). A page about one thing, or an app
 * the suite lacks, goes to the suite's Home on the phone (its own Home is
 * what makes it that suite) and Today on a desktop. A switch made from the
 * phone's Apps screen (`fromLauncher`) goes nowhere: the screen stays open and
 * shows the new suite's apps (it closes when the route changes).
 */
export const landingAfterSwitch = (
  pathname: string,
  phone: boolean,
  apps: readonly { to: string; exact?: boolean }[] | null,
  fromLauncher = false,
): string => {
  if (fromLauncher && phone) return pathname
  const home = phone ? '/home' : '/'
  if (ONE_THING.test(pathname)) return home
  if (pathname.startsWith('/settings') || pathname === '/search') return pathname
  if (!apps || apps.some((a) => navActive(a.to, pathname, a.exact))) return pathname
  return home
}

/** The digit that picks a suite from the keyboard: 1–9 by position, 0 for Everything. */
export const suiteDigit = (slug: string, ordered: readonly SuiteLike[]): string | undefined => {
  if (slug === ALL) return '0'
  const i = ordered.findIndex((w) => w.spec.slug === slug)
  return i >= 0 && i < 9 ? String(i + 1) : undefined
}

/** The slug a digit picks, if any. */
export const suiteForDigit = (digit: string, ordered: readonly SuiteLike[]): string | undefined => {
  if (digit === '0') return ALL
  const n = Number(digit)
  if (!Number.isInteger(n) || n < 1 || n > 9) return undefined
  return ordered[n - 1]?.spec.slug
}

/**
 * A suite's card: its wallpaper and the first apps of its dock past Home
 * (Home is in every dock), read from that suite's own Home.
 */
export const suitePreview = (
  settings: PhoneSettings,
  slug: string,
  has: (id: string) => boolean,
  max = 3,
) => {
  const layout = sanitise(storedPhone(settings, slug))
  return {
    wallpaper: layout.wallpaper,
    apps: layout.dock.filter((id) => id !== 'home' && has(id)).slice(0, max),
  }
}
